import ASTExplorer from "./ast_explorer";

export default class ASTBuilder {
  constructor(query) {
    this.query = query + " ";
  }

  isWordChar(char) {
    const code = char.charCodeAt(0);
    return (
      (code >= 48 && code <= 57) || // numeric (0-9)
      (code >= 65 && code <= 90) || // uppercase (A-Z)
      (code >= 97 && code <= 122) || // lowercase (a-z)
      code === 95 // underscore (_)
    );
  }

  isStringChar(char) {
    return char === '"' || char === "'" || char === "`";
  }

  isSpaceChar(char) {
    return char === "\t" || char === " " || char === "\n";
  }

  lastChild(ast) {
    return ast.children[ast.children.length - 1];
  }

  rootToken(ast) {
    return ASTExplorer.rootFnToken(ast)?.token;
  }

  childPush(ast, child) {
    child.parent = ast;

    if (ast?.children?.length > 0) {
      child.tokenIdx = ast.children.length;
      const lastChild = this.lastChild(ast);
      lastChild.next = child;
      child.prev = lastChild;
    } else {
      child.tokenIdx = 0;
    }

    ast?.children?.push(child);

    return child;
  }

  process() {
    let token = "";
    let rootAst = { children: [] };
    let queryLen = this.query.length;

    // Pointer to the current ast node
    let ast = this.childPush(rootAst, {
      start: 0,
      type: "rootFn",
      children: [],
    });
    let prevChar;

    const closeASTContents = (pos) => {
      ast.contents = token;
      ast.end = pos;
      ast.open = false;
      ast = ast.parent;
      token = "";
    };

    const pushToken = (pos) => {
      if (token !== "") {
        this.childPush(ast, {
          start: pos - token.length,
          end: pos - 1,
          type: "token",
          token,
        });
        token = "";
      }
    };

    for (let pos = 0; pos < queryLen; pos++) {
      let char = this.query[pos];

      // Inside string
      //
      if (ast.type === "string") {
        if (
          this.isStringChar(char) &&
          char === ast.stringChar &&
          prevChar !== "\\"
        ) {
          closeASTContents(pos);
        } else {
          token += char;
        }

        // Inside regex
        //
      } else if (ast.type === "regex") {
        if (char === "/" && prevChar !== "\\") {
          closeASTContents(pos);
        } else {
          token += char;
        }

        // Normal tokens
        //
      } else {
        if (this.isSpaceChar(char)) {
          pushToken(pos);
        } else if (char === ",") {
          pushToken(pos);
          this.childPush(ast, {
            start: pos,
            end: pos,
            type: "token",
            token: ",",
          });
        } else if (char === "|") {
          ASTExplorer.walkParents(ast, (parent) => {
            if (parent.open || parent.type === "rootFn") {
              parent.end = pos - 1;
            }
          });
          ast = this.childPush(rootAst, {
            start: pos + 1,
            type: "rootFn",
            children: [],
          });
        } else if (char === '"' || char === "'" || char === "`") {
          ast = this.childPush(ast, {
            start: pos,
            before: token,
            stringChar: char,
            open: true,
            type: "string",
          });
          token = "";
        } else if (
          char === "/" &&
          (this.rootToken(ast) === "parse" ||
            this.lastChild(ast)?.token == "match")
        ) {
          ast = this.childPush(ast, { start: pos, open: true, type: "regex" });
        } else if (char === ")" || char === "]" || char === "}") {
          pushToken(pos);

          if (ast.open) {
            ast.end = pos;
            ast.open = false;
            ast = ast.parent;
          }
        } else if (char === "(" && this.isWordChar(prevChar)) {
          ast = this.childPush(ast, {
            start: pos - token.length,
            open: true,
            type: "exprFn",
            tokenEnd: pos,
            token,
            children: [],
          });
          token = "";
        } else if (char === "[") {
          ast = this.childPush(ast, {
            start: pos,
            open: true,
            type: "array",
            children: [],
          });
          token = "";
        } else if (char === "{") {
          ast = this.childPush(ast, {
            start: pos,
            open: true,
            type: "block",
            children: [],
          });
          token = "";
        } else if (char === "(") {
          ast = this.childPush(ast, {
            start: pos,
            open: true,
            type: "group",
            children: [],
          });
          token = "";
        } else {
          token += char;
        }
      }

      // The end
      //
      if (pos === queryLen - 1) {
        // Update any open groups
        if (token !== "" && (ast.type === "string" || ast.type === "regex")) {
          ast.contents = token;
        }

        ASTExplorer.walkParents(ast, (parent) => {
          if (parent.open || parent.type === "rootFn") {
            parent.end = pos;
          }
        });

        pushToken(pos);
        ASTExplorer.findParent(ast, "rootFn").end = pos;
      }

      prevChar = char;
    }

    this.ast = rootAst;
  }

  getCursorInfo(query, cursorOffset) {
    const before = query.substring(0, cursorOffset);
    const lines = before.split("\n");
    return {
      offset: cursorOffset,
      line: lines.length,
      column: lines[lines.length - 1].length + 1,
    };
  }

  findIdx(idx) {
    const found = this._findIdx(this.ast.children, idx);

    if (found.children !== undefined && found.children.length > 0) {
      if (idx <= found.children[0].start) {
        found.cursor = { type: "cursor", pos: idx, next: found.children[0] };
      } else if (idx >= found.children[found.children.length - 1].end) {
        found.cursor = {
          type: "cursor",
          pos: idx,
          prev: found.children[found.children.length - 1],
        };
      }

      for (let i = 0; i < found.children.length - 1; i++) {
        const c1 = found.children[i];
        const c2 = found.children[i + 1];

        if (idx >= c1.end && idx <= c2.start) {
          found.cursor = { type: "cursor", pos: idx, prev: c1, next: c2 };
        }
      }
    } else if (
      found.type === "regex" ||
      found.type === "string" ||
      found.type === "group"
    ) {
      if (idx === found?.start || idx - 1 === found?.end) {
        found.outside = true;
      }
    } else if (found.type === "token" || found.type === "exprFn") {
      const end = found?.tokenEnd || found?.end;
      if (idx == found.start) {
        found.beginningOfToken = true;
      } else if (idx <= end) {
        found.withinToken = true;
      }
    }

    return found;
  }

  _findIdx(children, idx) {
    let found;
    children.every((child) => {
      const match = idx >= child.start && idx - 1 <= child.end;
      if (match && child?.children?.length > 0) {
        let other = this._findIdx(child.children, idx);
        if (other !== undefined) {
          other.idxOffset = idx - other.start;
          found = other;
          return false;
        }
      }

      // If we're at the end of a function, we want to return the parent
      if (match && child.type === "exprFn" && idx - 1 === child.end) {
        found = child.parent;
        return false;
      } else if (match) {
        found = child;
        return false;
      }

      return true;
    });

    if (found !== undefined) {
      found.cursorInfo = this.getCursorInfo(this.query, idx);
    }

    return found;
  }

  get flatAST() {
    return this.withoutRefs(this.ast);
  }

  withoutRefs(ast) {
    // Return the ast directly if it's falsy or has no children
    if (!ast || ast.children === undefined) {
      return ast;
    }

    // Map over the children to create a new array
    const newChildren = ast.children.map((tok) => {
      // eslint-disable-next-line no-unused-vars
      const { cursor, parent, prev, next, ...tokWithoutRefs } = tok;
      return this.withoutRefs(tokWithoutRefs);
    });

    // Return a new ast object with the updated children
    return { ...ast, children: newChildren };
  }
}
